import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { getQuestionsFromStorage, getCategoriesFromStorage, StoredQuestion, StoredCategory } from '../utils/storage';
import { ExamQuestion } from '../types';
import { saveRecord } from '../utils/storage';
import { getExamByKey, getSessionExamIds } from '../data/examCatalog';
import { fetchExamData, getExamIdFromType, QuestionMaster } from '../utils/questionApi';

type Step = 'category' | 'exam' | 'result';

// Exam types available in the premium page
type ExamType = { id: string; examId: number; name: string; emoji: string };

const EXAM_TYPES: ExamType[] = [
  { id: 'saa', examId: 1, name: 'AWS SAA-C03', emoji: '🏗️' },
  { id: 'sap', examId: 2, name: 'AWS SAP-C02', emoji: '🎓' },
  { id: 'ccp', examId: 3, name: 'AWS CCP', emoji: '☁️' },
  { id: 'dva', examId: 4, name: 'AWS DVA', emoji: '💻' },
];

const SESSION_STEP_KEY = 'premium_step';
const SESSION_CAT_KEY  = 'premium_cat';
const SESSION_EXAM_KEY = 'premium_exam';
const SESSION_ANSWERS_KEY = 'premium_answers';

/* ─── Static emoji mapping for SAP categories ─── */
const CATEGORY_EMOJIS: Record<number, string> = {
  101: '🏗️',  // Design for Organizational Complexity
  102: '🛠️',  // Design for New Solutions
  103: '🚚',  // Migration Planning
  104: '💰',  // Cost Optimization
  105: '🔒',  // Security & Compliance
  106: '🌐',  // Network & Hybrid Architecture
  107: '🏦',  // Multi-Account Strategy
  108: '🔄',  // Business Continuity
  109: '⚙️',  // Deployment & Operations
  110: '🛡️',  // Data Protection & Privacy
};

const DEFAULT_CATEGORY_EMOJI = '📁';

// Helper function to get emoji for a category
export const getCategoryEmoji = (categoryId: number): string => {
  return CATEGORY_EMOJIS[categoryId] || DEFAULT_CATEGORY_EMOJI;
};

/* ─── Auth guard ─── */
function isLoggedIn(): boolean {
  // Check multiple auth keys - userLoggedIn from LoginPage, userAuth, and localStorage
  return sessionStorage.getItem('userLoggedIn') === '1' ||
         sessionStorage.getItem('userAuth') === '1' ||
         localStorage.getItem('360digi_user_auth') === '1';
}

const PremiumExamPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Determine exam type from URL path ── */
  const getExamTypeFromPath = (): string => {
    const path = location.pathname;
    // Match paths like /aws-exam-questions-saa, /aws-exam-questions-sap, etc.
    const match = path.match(/\/aws-exam-questions-(\w+)/);
    if (match) {
      const examType = match[1].toLowerCase();
      // Validate it's a known exam type
      const validTypes = ['saa', 'sap', 'ccp', 'dva'];
      if (validTypes.includes(examType)) {
        return examType;
      }
    }
    return 'sap'; // default to SAP
  };

  /* ── Redirect immediately if not logged in ── */
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  /* ── Restore step + category from sessionStorage on refresh ── */
  const restoredStep = (sessionStorage.getItem(SESSION_STEP_KEY) as Step | null) ?? 'category';
  const restoredCat  = sessionStorage.getItem(SESSION_CAT_KEY) ?? null;
  const restoredExamType = sessionStorage.getItem(SESSION_EXAM_KEY);
  const restoredAnswers = sessionStorage.getItem(SESSION_ANSWERS_KEY);

  const [step, setStep]             = useState<Step>(restoredStep);
  const [currentCat, setCurrentCat] = useState<string | null>(restoredCat);
  const [selectedExamType, setSelectedExamType] = useState<string>(() => {
    // Check URL first - if it explicitly specifies an exam type, use it
    const match = location.pathname.match(/\/aws-exam-questions-(\w+)/);
    if (match) {
      const examType = match[1].toLowerCase();
      const validTypes = ['saa', 'sap', 'ccp', 'dva'];
      if (validTypes.includes(examType)) return examType;
    }
    // Fallback to sessionStorage (for mid-exam refresh)
    if (restoredExamType) return restoredExamType;
    // Default to sap
    return 'sap';
  });
  /* ── Redirect if the learner has not been given this exam ── */
  useEffect(() => {
    const exam = getExamByKey(selectedExamType);
    if (exam && !getSessionExamIds().includes(exam.id)) navigate('/dashboard', { replace: true });
  }, [selectedExamType, navigate]);

  const [userAnswers, setUserAnswers] = useState<Record<number, number | number[]>>(
    () => {
      try {
        return restoredAnswers ? JSON.parse(restoredAnswers) : {};
      } catch {
        return {};
      }
    }
  );
  const [checked, setChecked]       = useState<Record<number, boolean>>({});
  const [submitted, setSubmitted]   = useState(false);
  const [scoreData, setScoreData]   = useState<{ score: number; total: number } | null>(null);
  const { msg, type, visible, showToast } = useToast();

  /* ── Load custom questions from localStorage ── */
  const [customQuestions, setCustomQuestions] = useState<StoredQuestion[]>([]);
  const [customCategories, setCustomCategories] = useState<StoredCategory[]>([]);

  /* ── Exam type selection ── */
  const [dbQuestions, setDbQuestions] = useState<QuestionMaster[]>([]);
  const [dbCategories, setDbCategories] = useState<{ category_id: number; label: string; count: number }[]>([]);
  const [loadingDb, setLoadingDb] = useState(false);
  
  // Track last loaded exam type to prevent duplicate API calls
  const lastLoadedExam = React.useRef<string | null>(null);

  useEffect(() => {
    setCustomQuestions(getQuestionsFromStorage());
    setCustomCategories(getCategoriesFromStorage());
  }, []);

  /* ── Fetch questions from database - handles page refresh properly ── */
  useEffect(() => {
    const loadDbQuestions = async () => {
      setLoadingDb(true);
      
      // Check if we're restoring a session (page was refreshed mid-exam)
      const isMidExam = (step === 'exam' || step === 'result') && restoredCat;
      
      // Only reset to category selection if starting fresh
      if (!isMidExam) {
        setCurrentCat(null);
        sessionStorage.removeItem(SESSION_CAT_KEY);
        setStep('category');
      } else {
        // Restore the category when refreshing mid-exam
        setCurrentCat(restoredCat);
      }
      
      try {
        const examId = getExamIdFromType(selectedExamType);
        
        console.log('Loading exam:', selectedExamType, 'with examId:', examId);
        
        const result = await fetchExamData(examId);
        
        console.log('Fetch exam data result:', result);
        
        if (result.success && result.questions) {
          console.log('Questions loaded:', result.questions.length);
          console.log('Categories loaded:', result.categories?.length);
          setDbQuestions(result.questions);
          setDbCategories(result.categories || []);
        } else {
          console.error('Failed to fetch exam data:', result.error);
          setDbQuestions([]);
          setDbCategories([]);
        }
      } catch (error) {
        console.error('Error loading database questions:', error);
        setDbQuestions([]);
        setDbCategories([]);
      } finally {
        setLoadingDb(false);
      }
    };
    
    // Load when selectedExamType changes
    loadDbQuestions();
  }, [selectedExamType]);

   /* ── Persist step & category whenever they change ── */
  useEffect(() => {
    sessionStorage.setItem(SESSION_STEP_KEY, step);
  }, [step]);

  /* ── Update URL when exam type changes (for UI selector) ── */
  useEffect(() => {
    const expectedPath = `/aws-exam-questions-${selectedExamType}`;
    if (location.pathname !== expectedPath) {
      navigate(expectedPath, { replace: true });
    }
  }, [selectedExamType, location.pathname, navigate]);

  useEffect(() => {
    if (currentCat) sessionStorage.setItem(SESSION_CAT_KEY, currentCat);
    else            sessionStorage.removeItem(SESSION_CAT_KEY);
  }, [currentCat]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_EXAM_KEY, selectedExamType);
  }, [selectedExamType]);

  useEffect(() => {
    if (Object.keys(userAnswers).length > 0) {
      sessionStorage.setItem(SESSION_ANSWERS_KEY, JSON.stringify(userAnswers));
    } else {
      sessionStorage.removeItem(SESSION_ANSWERS_KEY);
    }
  }, [userAnswers]);

  /* ── If restored to exam/result but cat is missing, fall back to category ── */
  useEffect(() => {
    if ((step === 'exam' || step === 'result') && !currentCat) {
      setStep('category');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startExam = (catId: string) => {
    setCurrentCat(catId);
    setUserAnswers({});
    setChecked({});
    setSubmitted(false);
    setScoreData(null);
    setStep('exam');
    sessionStorage.setItem(SESSION_CAT_KEY, catId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isMultiSelect = (catId: string, qIdx: number): boolean => {
    // Check if it's a DB question
    const data = getCombinedData();
    if (data && data.questions[qIdx]) {
      const q = data.questions[qIdx] as any;
      if (q._isMultiSelect !== undefined) return q._isMultiSelect;
    }
    return false;
  };

  const getCorrectIndices = (catId: string, qIdx: number): number[] => {
    // Check if it's a DB question
    const data = getCombinedData();
    if (data && data.questions[qIdx]) {
      const q = data.questions[qIdx] as any;
      if (q._correctAnswers !== undefined) return q._correctAnswers;
    }
    return [0];
  };

  const selectAnswer = (qIdx: number, optIdx: number) => {
    if (checked[qIdx]) return;
    const catId = currentCat!;
    if (isMultiSelect(catId, qIdx)) {
      setUserAnswers(prev => {
        const current = (prev[qIdx] as number[]) || [];
        const exists  = current.includes(optIdx);
        return { ...prev, [qIdx]: exists ? current.filter(i => i !== optIdx) : [...current, optIdx] };
      });
    } else {
      setUserAnswers(prev => ({ ...prev, [qIdx]: optIdx }));
    }
  };

  const checkAnswer = (qIdx: number) => {
    const catId  = currentCat!;
    const answer = userAnswers[qIdx];
    if (answer === undefined || (Array.isArray(answer) && answer.length === 0)) {
      showToast('⚠️ Please select an answer first.', 'error');
      return;
    }
    if (isMultiSelect(catId, qIdx)) {
      const required = getCorrectIndices(catId, qIdx).length;
      if ((answer as number[]).length !== required) {
        showToast(`⚠️ This question requires ${required} answers.`, 'error');
        return;
      }
    }
    setChecked(prev => ({ ...prev, [qIdx]: true }));
  };

  const showAnswerDirect = (qIdx: number) =>
    setChecked(prev => ({ ...prev, [qIdx]: true }));

  const submitExam = useCallback(() => {
    if (!currentCat) return;
    const examData = getCombinedData();
    if (!examData) return;
    const total = examData.questions.length;
    let score   = 0;

    const allChecked: Record<number, boolean> = {};
    examData.questions.forEach((_, i: number) => { allChecked[i] = true; });
    setChecked(allChecked);
    setSubmitted(true);

    // Database questions only
    const categoryIdNum = parseInt(currentCat);
    const dbQ = dbQuestions.filter(q => q.category_id === categoryIdNum);
    
    examData.questions.forEach((q, i: number) => {
      // DB question only
      const dq = dbQ[i];
      const correct = dq.correct_ans.split(',').map(a => parseInt(a.trim()) - 1);
      
      const ans     = userAnswers[i];
      if (Array.isArray(ans)) {
        if (JSON.stringify([...ans].sort()) === JSON.stringify([...correct].sort())) score++;
      } else if (ans !== undefined && correct.includes(ans as number)) {
        score++;
      }
    });

    saveRecord('exam_submissions', {
      category:     currentCat,
      categoryName: examData.name,
      score, total,
      percentage:   Math.round((score / total) * 100),
      answers:      { ...userAnswers },
      source:       'premium_exam',
    });

    setScoreData({ score, total });
    setStep('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentCat, userAnswers, dbQuestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const backToCategories = () => {
    setCurrentCat(null);
    setUserAnswers({});
    setChecked({});
    setSubmitted(false);
    setScoreData(null);
    setStep('category');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── Convert DB question to exam format ── */
  const convertDbQuestion = (q: QuestionMaster, catId: string) => {
    const options = [
      q.option1,
      q.option2,
      q.option3,
      q.option4,
      q.option5,
      q.option6,
    ].filter(opt => opt && opt.trim());
    
    // Parse correct answer (can be "1" or "1,2" for multi-select)
    const correctAnswers = q.correct_ans.split(',').map(a => parseInt(a.trim()) - 1);
    
    return {
      q: q.question,
      opts: options,
      ans: correctAnswers[0],
      explain: q.explaination,
      _isMultiSelect: correctAnswers.length > 1,
      _correctAnswers: correctAnswers,
      _dbId: q.id,
      _categoryId: catId,
    };
  };

  /* ── Build exam data from database only (no custom or static questions) ── */
  const getCombinedData = () => {
    if (!currentCat) return null;
    
    // Get database questions for this category only
    const categoryIdNum = parseInt(currentCat);
    const dbQ = dbQuestions.filter(q => q.category_id === categoryIdNum);
    
    // If no DB questions, return null
    if (dbQ.length === 0) return null;
    
    // Build questions array - only database questions
    const dbQuestionsFormatted = dbQ.map(q => convertDbQuestion(q, currentCat));
    
    // Get category info
    const dbCat = dbCategories.find(c => c.category_id === categoryIdNum);
    const emoji = getCategoryEmoji(categoryIdNum);
    const categoryInfo = { name: dbCat?.label || 'Database Category', emoji };
    
    return {
      name: categoryInfo?.name || 'Database Category',
      emoji: categoryInfo?.emoji || emoji,
      color: 'var(--blue)',
      questions: dbQuestionsFormatted,
      _source: 'database'
    };
  };

  /* ── Get multi-select check for DB questions ── */
  const isMultiSelectDb = (qIdx: number, allQuestions: (ExamQuestion & { _isMultiSelect?: boolean; _correctAnswers?: number[] })[]): boolean => {
    const q = allQuestions[qIdx];
    return (q as any)._isMultiSelect === true;
  };

  /* ── Get correct indices for DB questions ── */
  const getCorrectIndicesDb = (qIdx: number, allQuestions: (ExamQuestion & { _isMultiSelect?: boolean; _correctAnswers?: number[] })[]): number[] => {
    const q = allQuestions[qIdx];
    const dbCorrect = (q as any)._correctAnswers;
    if (dbCorrect) return dbCorrect;
    return [q.ans];
  };

  /* ── Get categories (database only - no static questions) ── */
  const getCombinedCategories = () => {
    // Get database categories only - these come from Supabase
    const dbCats = dbCategories.map(c => ({
      id: String(c.category_id),
      name: c.label,
      emoji: getCategoryEmoji(c.category_id),
      count: `${c.count} Questions`
    }));
    
    // Return only database categories, no custom or static
    return dbCats;
  };

  const data       = getCombinedData();
  const totalQ     = data?.questions.length ?? 0;
  const answeredCount = Object.keys(userAnswers).filter(k => {
    const v = userAnswers[Number(k)];
    return v !== undefined && !(Array.isArray(v) && v.length === 0);
  }).length;

  /* ── Don't render anything until auth is confirmed ── */
  if (!isLoggedIn()) return null;

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <Layout>
      <Toast msg={msg} type={type} visible={visible} />

      <div className="page-hero" style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', color: '#fff' }}>
        <span className="section-tag" style={{ background: 'rgba(59,130,246,0.2)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
          🏆 Premium Exam Bank
        </span>
        <h1 style={{ color: '#fff' }}>
          {EXAM_TYPES.find(e => e.id === selectedExamType)?.name || 'AWS'} <span style={{ color: '#fdba74' }}>Premium</span> Questions
        </h1>
        <p style={{ color: '#94a3b8' }}>
          Scenario-based questions covering encryption, networking, multi-account design, serverless, and governance.
        </p>
      </div>

      <div className="section">
        <div className="container" style={{ maxWidth: '100%', padding: '0 20px' }}>

          {/* ══ CATEGORY SELECTION ══ */}
          {step === 'category' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '4px' }}>
                    🏆 Premium Question Bank
                  </h2>
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem' }}>
                    Advanced scenario-based AWS questions. Select a topic to begin.
                  </p>
                </div>
                <button className="btn btn-outline-gray btn-sm" onClick={() => navigate('/login')}>
                  ← Dashboard
                </button>
              </div>

              {/* Exam Type Selector */}
              <div style={{ marginBottom: '24px', padding: '16px', background: 'linear-gradient(135deg, var(--blue-pale), #e0f2fe)', border: '1px solid var(--blue-soft)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '12px' }}>
                  📚 Select Exam Type:
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {EXAM_TYPES.map(exam => (
                    <button
                      key={exam.id}
                      onClick={() => {
                        setSelectedExamType(exam.id);
                        setCurrentCat(null);
                      }}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '50px',
                        border: selectedExamType === exam.id ? '2px solid var(--blue)' : '1.5px solid var(--gray-300)',
                        background: selectedExamType === exam.id ? 'var(--blue)' : 'white',
                        color: selectedExamType === exam.id ? 'white' : 'var(--gray-700)',
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span>{exam.emoji}</span>
                      <span>{exam.name}</span>
                    </button>
                  ))}
                </div>
                  {loadingDb && (
                    <div style={{ textAlign: 'center', padding: '20px', margin: '10px 0' }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>⏳</div>
                      <p style={{ color: 'var(--gray-600)' }}>Loading questions from database...</p>
                    </div>
                  )}
              </div>

                {getCombinedCategories().length === 0 && !loadingDb && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', background: 'var(--gray-100)', borderRadius: 'var(--radius)', margin: '20px 0' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📭</div>
                    <h3 style={{ color: 'var(--gray-700)', marginBottom: '12px' }}>No Questions Found</h3>
                    <p style={{ color: 'var(--gray-500)', marginBottom: '16px' }}>
                      No premium questions available for this exam yet.<br/>
                      Questions will be added soon!
                    </p>
                    <button className="btn btn-blue" onClick={() => { setSelectedExamType('saa'); setCurrentCat(null); }}>
                      Try Another Exam Type
                    </button>
                  </div>
                )}
                {getCombinedCategories().length > 0 && (
                  <div className="exam-cat-grid">
                    {getCombinedCategories().map((cat: { id: string; name: string; emoji: string; count: string }) => (
                      <button key={cat.id} className="exam-cat-card" onClick={() => startExam(cat.id)}>
                        <div className="cat-emoji">{cat.emoji}</div>
                        <div className="cat-name">{cat.name}</div>
                        <div className="cat-count">{cat.count}</div>
                      </button>
                    ))}
                  </div>
                )}

              <div style={{ marginTop: '28px', padding: '18px', background: 'linear-gradient(135deg, var(--blue-pale), var(--orange-pale))', border: '1.5px solid var(--blue-soft)', borderRadius: 'var(--radius)' }}>
                <p style={{ fontSize: '0.88rem', color: 'var(--gray-700)', fontWeight: 600 }}>
                  💡 <strong>Tip:</strong> These are advanced scenario questions modelled after the AWS SAA-C03 exam. Read each scenario carefully before selecting your answer.
                </p>
              </div>
            </div>
          )}

          {/* ══ EXAM QUESTIONS ══ */}
          {step === 'exam' && data && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <button className="btn btn-outline-gray btn-sm" onClick={backToCategories}>← Back to Topics</button>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 600 }}>
                    {answeredCount} of {totalQ} answered
                  </div>
                  <div className="progress-bar-wrap" style={{ width: '180px', marginTop: '5px' }}>
                    <div className="progress-bar-fill" style={{ width: `${Math.round((answeredCount / totalQ) * 100)}%` }} />
                  </div>
                </div>
              </div>

              {/* Category badge */}
              <div style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', border: '1px solid #334155', borderRadius: 'var(--radius)', padding: '14px 18px', marginBottom: '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2rem' }}>{data.emoji}</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#93c5fd' }}>{data.name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{data.questions.length} questions · Check each answer individually</div>
                </div>
              </div>

              {data.questions.map((q: ExamQuestion, qIdx: number) => {
                const isCheckedQ  = checked[qIdx];
                const isMS        = isMultiSelect(currentCat!, qIdx);
                const correct     = getCorrectIndices(currentCat!, qIdx);
                const selected    = userAnswers[qIdx];
                const selectedArr = Array.isArray(selected) ? selected : (selected !== undefined ? [selected as number] : []);

                const isAnswerCorrect = (() => {
                  if (isMS) return JSON.stringify([...selectedArr].sort()) === JSON.stringify([...correct].sort());
                  return selectedArr.length > 0 && correct.includes(selectedArr[0]);
                })();

                return (
                  <div className="q-card" key={qIdx}>
                    <div className="q-header">
                      <div className="q-num" style={isCheckedQ ? { background: isAnswerCorrect ? 'var(--green-pale)' : 'var(--red-pale)', color: isAnswerCorrect ? 'var(--green)' : 'var(--red)' } : {}}>
                        {isCheckedQ ? (isAnswerCorrect ? '✓' : '✗') : qIdx + 1}
                      </div>
                      <div className="q-text" style={{ whiteSpace: 'pre-wrap' }}>
                        {isMS && <span style={{ fontSize: '0.74rem', fontWeight: 700, background: 'var(--orange-pale)', color: 'var(--orange)', padding: '2px 8px', borderRadius: '50px', marginRight: '8px' }}>Choose {correct.length}</span>}
                        {q.q}
                      </div>
                    </div>

                    <div className="q-options">
                      {q.opts.map((opt: string, optIdx: number) => {
                        let cls = '';
                        if (isCheckedQ) {
                          if (correct.includes(optIdx)) cls = 'correct';
                          else if (selectedArr.includes(optIdx) && !correct.includes(optIdx)) cls = 'wrong';
                        } else if (selectedArr.includes(optIdx)) {
                          cls = 'selected';
                        }
                        return (
                          <div
                            key={optIdx}
                            className={`q-opt${cls ? ' ' + cls : ''}`}
                            onClick={() => !isCheckedQ && selectAnswer(qIdx, optIdx)}
                            style={isCheckedQ ? { pointerEvents: 'none' } : {}}
                          >
                            <input
                              type={isMS ? 'checkbox' : 'radio'}
                              readOnly
                              checked={selectedArr.includes(optIdx)}
                              style={{ accentColor: 'var(--blue)', width: '15px', height: '15px', flexShrink: 0 }}
                            />
                            <span style={{ whiteSpace: 'pre-wrap' }}>{String.fromCharCode(65 + optIdx)}. {opt}</span>
                          </div>
                        );
                      })}
                    </div>

                    {isCheckedQ ? (
                      <div className={`q-answer-box show ${isAnswerCorrect ? 'correct-ans' : 'wrong-ans'}`}>
                        {isAnswerCorrect
                          ? <><strong>✅ Correct!</strong></>
                          : <><strong>❌ Incorrect.</strong> Correct answer{correct.length > 1 ? 's' : ''}: <strong>{correct.map(i => String.fromCharCode(65 + i)).join(', ')}</strong></>
                        }
                        <br /><br /><strong>Explanation:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{q.explain}</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                        <button className="btn btn-blue btn-sm" onClick={() => checkAnswer(qIdx)}>Check Answer</button>
                        <button className="btn btn-outline-gray btn-sm" onClick={() => showAnswerDirect(qIdx)}>Show Answer</button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '24px' }}>
                <button className="btn btn-blue btn-lg" onClick={submitExam} disabled={submitted}>
                  Submit All &amp; See Score
                </button>
                <button className="btn btn-outline-gray" onClick={backToCategories}>Try Another Topic</button>
              </div>
            </div>
          )}

          {/* ══ RESULTS ══ */}
          {step === 'result' && data && scoreData && (() => {
            const pct   = Math.round((scoreData.score / scoreData.total) * 100);
            const wrong = scoreData.total - scoreData.score;
            let emoji = '📚', title = 'Keep Practicing!', resultMsg = "Review the explanations and try again. These are advanced scenarios — don't be discouraged!";
            if (pct >= 80) { emoji = '🏆'; title = 'Outstanding!';    resultMsg = "Excellent work! You're well-prepared for the AWS SAA exam."; }
            else if (pct >= 60) { emoji = '👍'; title = 'Good Progress!'; resultMsg = 'Solid attempt. Review the explanations to strengthen your weak areas.'; }
            return (
              <div>
                <button className="btn btn-outline-gray btn-sm" onClick={backToCategories} style={{ marginBottom: '18px' }}>← Try Another Topic</button>
                <div className="score-card">
                  <div style={{ fontSize: '3rem', marginBottom: '8px' }}>{emoji}</div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-800)' }}>{title} — {data.name}</h2>
                  <div style={{ fontSize: '3.2rem', fontWeight: 800, color: 'var(--blue)', margin: '14px 0', fontFamily: "'Sora',sans-serif" }}>
                    {scoreData.score}/{scoreData.total} ({pct}%)
                  </div>
                  <p style={{ color: 'var(--gray-600)', maxWidth: '460px', margin: '0 auto 22px' }}>{resultMsg}</p>

                  <div style={{ maxWidth: '380px', margin: '0 auto 24px' }}>
                    {[
                      { label: '✅ Correct', count: scoreData.score, pct, color: 'var(--green)' },
                      { label: '❌ Wrong',   count: wrong, pct: (wrong / scoreData.total) * 100, color: 'var(--red)' },
                    ].map((bar, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                        <span style={{ fontSize: '0.84rem', fontWeight: 700, minWidth: '80px', color: 'var(--gray-700)' }}>{bar.label}</span>
                        <div style={{ flex: 1, background: 'var(--gray-200)', borderRadius: '50px', height: '10px' }}>
                          <div style={{ height: '10px', borderRadius: '50px', background: bar.color, width: `${bar.pct}%` }} />
                        </div>
                        <span style={{ fontSize: '0.82rem', color: 'var(--gray-500)', fontWeight: 700, minWidth: '28px' }}>{bar.count}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button className="btn btn-blue" onClick={backToCategories}>Try Another Topic</button>
                    <Link to="/request?tab=session" className="btn btn-orange">Book 1-on-1 Session</Link>
                  </div>
                </div>

                {/* Detailed Review */}
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '16px', color: 'var(--gray-800)' }}>📋 Detailed Answer Review</h3>
                  {data.questions.map((q: ExamQuestion, i: number) => {
                    const correct   = getCorrectIndices(currentCat!, i);
                    const ans       = userAnswers[i];
                    const ansArr    = Array.isArray(ans) ? ans : (ans !== undefined ? [ans as number] : []);
                    const isCorrect = Array.isArray(ans)
                      ? JSON.stringify([...ansArr].sort()) === JSON.stringify([...correct].sort())
                      : ansArr.length > 0 && correct.includes(ansArr[0]);

                    return (
                      <div className="q-card" key={i}>
                        <div className="q-header">
                          <div className="q-num" style={{ background: isCorrect ? 'var(--green-pale)' : 'var(--red-pale)', color: isCorrect ? 'var(--green)' : 'var(--red)' }}>
                            {isCorrect ? '✓' : '✗'}
                          </div>
                          <div className="q-text" style={{ whiteSpace: 'pre-wrap' }}>{q.q}</div>
                        </div>
                        <div style={{ fontSize: '0.85rem', marginBottom: '6px', color: isCorrect ? '#166534' : 'var(--red)' }}>
                          {isCorrect
                            ? '✅ You answered correctly!'
                            : `❌ Your answer: ${ansArr.length > 0 ? ansArr.map(a => `${String.fromCharCode(65 + a)}. ${q.opts[a]}`).join(', ') : 'Not answered'}`
                          }
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '8px', fontWeight: 600 }}>
                          ✅ Correct: {correct.map(c => `${String.fromCharCode(65 + c)}. ${q.opts[c]}`).join(' | ')}
                        </div>
                        <div style={{ fontSize: '0.84rem', color: 'var(--gray-600)', background: 'var(--gray-100)', padding: '10px', borderRadius: '8px', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                          <strong>💡 Explanation:</strong> {q.explain}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

        </div>
      </div>
    </Layout>
  );
};

export default PremiumExamPage;
